import { useQuery } from "@apollo/client";
import { useAbility } from "@/partner/shared/lib";
import { BillingHasContractsQuery } from "../graphql/queries";
import type { BillingHasContractsQueryType } from "@/types/graphql/partner.generated";

/**
 * Есть ли у организации хоть один контракт (спека §10: «раздел скрыт
 * целиком, если у организации нет ни одного контракта»). Отдельного
 * запроса на число контрактов у партнёрского API нет и заводить его этой
 * правкой нельзя (бэкенд не трогаем), поэтому пользуемся тем, что уже
 * есть: организация без контракта не может иметь ни одного счёта, ни
 * одного отчёта по доле с продаж, и наоборот. Признак приблизительный:
 * контракт без единого невыставленного (не черновик) платежа или отчёта
 * даст здесь false, хотя контракт есть. Но до первого события по контракту
 * таблицы всё равно были бы пустыми, а раздел без данных сейчас прячется
 * тем же способом, что и раздел без контракта вовсе.
 *
 * Запрос уходит только при наличии права на чтение платежей: у роли без
 * доступа пункт меню и так скрыт ability-гейтом (см. filterMenuItems), а
 * прав read RevenueShareReport без read ContractPayment не бывает
 * (PartnerPolicy выдаёт оба в одной ветке isAdmin).
 */
export function useHasBillingContracts(): {
  hasContracts: boolean;
  loading: boolean;
} {
  const ability = useAbility();
  const skip = !ability.can("read", "ContractPayment");

  const { data, loading } = useQuery<BillingHasContractsQueryType>(
    BillingHasContractsQuery,
    { skip, fetchPolicy: "cache-first" }
  );

  if (skip) return { hasContracts: false, loading: false };

  // Пока ответ не пришёл, считаем, что раздел есть: как и у liveEnabled,
  // отказ вспомогательного запроса не должен на мгновение прятать пункт
  // меню у партнёра, которому он реально доступен.
  if (loading && !data) return { hasContracts: true, loading: true };

  const hasContracts =
    (data?.contractPayments.count ?? 0) > 0 ||
    (data?.revenueShareReports.count ?? 0) > 0;

  return { hasContracts, loading: false };
}
